import { useState, useEffect, useRef, useCallback } from "react";

// --- Types ---

interface AnimationStep {
  x: number;
  y: number;
  duration: number;
  ease: string;
  hold?: number; // ms to wait at this position before advancing to next step
}

interface AnimationStateDef {
  name: string;
  priority: number;
  sequence: AnimationStep[];
  nextState?: string; // default: "idle"
}

interface MousePosition {
  x: number;
  y: number;
}

// --- State Definitions ---
// To add a new behavior: just add an entry here and a trigger in the hook.

const STATES: Record<string, AnimationStateDef> = {
  idle: {
    name: "idle",
    priority: 0,
    sequence: [{ x: 0, y: 0, duration: 0.3, ease: "easeOut" }],
  },
  "bored-drop": {
    name: "bored-drop",
    priority: 1,
    sequence: [
      { x: 0, y: 50, duration: 4, ease: "linear" },
      { x: 0, y: 50, duration: 0, ease: "linear", hold: 2000 },
      { x: 0, y: 0, duration: 0.3, ease: "easeOut" },
    ],
    nextState: "idle",
  },
  "look-at-text": {
    name: "look-at-text",
    priority: 5,
    sequence: [
      { x: 15, y: -3, duration: 0.2, ease: "easeOut" },
      { x: 15, y: -3, duration: 0, ease: "linear", hold: 2000 },
      { x: 0, y: 0, duration: 0.3, ease: "easeOut" },
    ],
    nextState: "idle",
  },
  "follow-mouse": {
    name: "follow-mouse",
    priority: 10, // Highest priority - interrupts everything
    sequence: [{ x: 0, y: 0, duration: 0.15, ease: "easeOut" }], // Dynamic, updated continuously
    nextState: "follow-mouse", // Stay in this state — exit is handled by the hover trigger
  },
};

// --- Animation output (same shape as before) ---

export interface EyeAnimationConfig {
  x: number;
  y: number;
  transition: { duration: number; ease: string };
  scale?: number;
}

// --- Hook ---

export function useEyeAnimation(
  isLookingAtText: boolean,
  isHovered: boolean = false,
  mousePosition: MousePosition = { x: 0, y: 0 }
) {
  const [currentState, setCurrentState] = useState("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [animation, setAnimation] = useState<EyeAnimationConfig>({
    x: 0,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  });
  const [scale, setScale] = useState(1);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const currentPriorityRef = useRef(0);
  const wasHoveredRef = useRef(false);

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  // Start playing a state's sequence from step 0
  const playState = useCallback(
    (stateName: string) => {
      const def = STATES[stateName];
      if (!def) return;

      clearTimeouts();
      currentPriorityRef.current = def.priority;
      setCurrentState(stateName);
      setStepIndex(0);

      // Apply the first step immediately
      const firstStep = def.sequence[0];
      setAnimation({
        x: firstStep.x,
        y: firstStep.y,
        transition: { duration: firstStep.duration, ease: firstStep.ease },
      });
    },
    [clearTimeouts]
  );

  // Advance through the sequence
  useEffect(() => {
    const def = STATES[currentState];
    if (!def) return;

    const step = def.sequence[stepIndex];
    if (!step) return;

    // Calculate how long this step takes (animation duration + hold)
    const animationMs = step.duration * 1000;
    const holdMs = step.hold ?? 0;
    const totalMs = animationMs + holdMs;

    const nextIndex = stepIndex + 1;

    if (nextIndex < def.sequence.length) {
      // There's a next step — schedule it
      const t = setTimeout(() => {
        const nextStep = def.sequence[nextIndex];
        setAnimation({
          x: nextStep.x,
          y: nextStep.y,
          transition: { duration: nextStep.duration, ease: nextStep.ease },
        });
        setStepIndex(nextIndex);
      }, totalMs);
      timeoutsRef.current.push(t);
    } else {
      // Sequence complete — transition to next state
      const nextStateName = def.nextState ?? "idle";
      if (nextStateName !== currentState) {
        const t = setTimeout(() => {
          playState(nextStateName);
        }, totalMs);
        timeoutsRef.current.push(t);
      }
      // If nextState === currentState (e.g. idle → idle), we just stay.
      // Idle triggers are handled separately below.
    }

    return () => {
      // Cleanup only timeouts we added in this effect run
    };
  }, [currentState, stepIndex, playState]);

  // --- Triggers ---

  // Idle trigger: after random delay, start bored-drop
  useEffect(() => {
    if (currentState !== "idle") return;

    const delay = 20000 + Math.random() * 3000;
    const t = setTimeout(() => {
      // Only trigger if still idle (not interrupted)
      if (currentPriorityRef.current <= STATES["idle"].priority) {
        playState("bored-drop");
      }
    }, delay);
    timeoutsRef.current.push(t);

    return () => clearTimeout(t);
  }, [currentState, playState]);

  // External trigger: look-at-text
  useEffect(() => {
    if (!isLookingAtText) return;

    const targetDef = STATES["look-at-text"];
    // Only interrupt if look-at-text has higher priority than current
    if (targetDef.priority > currentPriorityRef.current) {
      const t = setTimeout(() => playState("look-at-text"), 0);
      timeoutsRef.current.push(t);
      return () => clearTimeout(t);
    }
  }, [isLookingAtText, playState]);

  // Mouse tracking trigger: follow-mouse (highest priority)
  useEffect(() => {
    if (isHovered) {
      // Enter follow-mouse state
      if (currentState !== "follow-mouse") {
        const targetDef = STATES["follow-mouse"];
        if (targetDef.priority > currentPriorityRef.current || !wasHoveredRef.current) {
          playState("follow-mouse");
          wasHoveredRef.current = true;
        }
      }

      // Calculate eye movement based on mouse position
      // Maximum movement: 8 units in each direction
      const maxMovement = 8;
      const eyeX = mousePosition.x * maxMovement;
      const eyeY = mousePosition.y * maxMovement;

      setAnimation({
        x: eyeX,
        y: eyeY,
        transition: { duration: 0.15, ease: "easeOut" },
      });

      // Scale up the eye when hovered
      setScale(1.3);
    } else if (wasHoveredRef.current) {
      // Exit follow-mouse state
      wasHoveredRef.current = false;
      setScale(1);

      // Return to idle if currently in follow-mouse
      if (currentState === "follow-mouse") {
        playState("idle");
      }
    }
  }, [isHovered, mousePosition, currentState, playState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimeouts();
  }, [clearTimeouts]);

  return {
    state: currentState,
    animation,
    scale,
  };
}
